import { motion } from '@eversports/react-components/design-tokens/motion'
import Button from '@eversports/react-components/primitives/Button'
import ErrorBox from '@eversports/react-components/primitives/ErrorBox'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Link from '@eversports/react-components/primitives/Link'
import Processing from '@eversports/react-components/primitives/Processing'
import Text from '@eversports/react-components/primitives/Text'
import { ClientError } from '@eversports/react-components/client-error'
import { makeStyles } from '@mui/styles'

import { Localized } from '../../localization/react'

export type PolicyVariant = 'terms' | 'privacy' | 'both'

export interface EversportsConsentUpdateFormProps {
  variant: PolicyVariant
  termsUrl?: string
  privacyUrl?: string
  loading?: boolean
  onConsentAcceptance: () => void
  onRemindMeLater: () => void
  error?: ClientError
  errorUrl: string
}

const useStyles = makeStyles({
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 24,
    maxWidth: 640,
  },
  warningBox: {
    backgroundColor: '#faf0f0',
    borderRadius: 8,
    padding: 16,
  },
})

const EversportsConsentUpdateForm = ({
  variant,
  termsUrl,
  privacyUrl,
  loading,
  onConsentAcceptance,
  onRemindMeLater,
  error,
  errorUrl,
}: EversportsConsentUpdateFormProps) => {
  const classes = useStyles()

  const titleId = `consent-update-title-${variant}` as const
  const introId = `consent-update-intro-${variant}` as const
  const reasonId = `consent-update-reason-${variant}` as const
  const ackExplanationId = `consent-update-acknowledge-explanation-${variant}` as const
  const remindExplanationId = `consent-update-remind-explanation-${variant}` as const

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ ease: 'easeInOut' }}
    >
      <Processing isLoading={loading}>
        <Flex flexDirection="column" verticalSpacing="default" className={classes.card}>
          <Flex alignItems="center" horizontalSpacing="dense">
            <Icon icon={Icons.INFO} color="text.primary" />
            <Heading is="h1" variant="normal" data-testid="consent-update-heading">
              <Localized id={titleId} />
            </Heading>
          </Flex>
          <Text color="text.secondary">
            <Localized id={introId} />
          </Text>
          <Flex flexDirection="column">
            {(variant === 'terms' || variant === 'both') && termsUrl && (
              <Link to={termsUrl} external blank>
                <Localized id="consent-update-link-terms" />
              </Link>
            )}
            {(variant === 'privacy' || variant === 'both') && privacyUrl && (
              <Link to={privacyUrl} external blank>
                <Localized id="consent-update-link-privacy" />
              </Link>
            )}
          </Flex>
          <Text color="text.secondary">
            <Localized id={reasonId} />
          </Text>
          <Text color="text.secondary" fontWeight="bold">
            <Localized id={ackExplanationId} />
          </Text>
          <Text color="text.secondary">
            <Localized id={remindExplanationId} />
          </Text>
          <Flex alignItems="flex-start" horizontalSpacing="default" className={classes.warningBox}>
            <Icon icon={Icons.ERROR_OUTLINE} color="danger" />
            <Text color="text.secondary">
              <Localized
                id="consent-update-remind-warning"
                params={{
                  boldPart: (
                    <Text is="span" fontWeight="bold">
                      <Localized id="consent-update-remind-warning-bold" />
                    </Text>
                  ),
                }}
              />
            </Text>
          </Flex>
          {error && (
            <ErrorBox>
              <Text color="danger" size="small" marginBottom="dense">
                <Localized id="consent-form-token-invalid-error" />
              </Text>
              <Link size="small" color="secondary" to={errorUrl}>
                <Localized id="consent-form-token-invalid-error-link" />
              </Link>
            </ErrorBox>
          )}
          <Flex
            flexDirection={['column', 'row']}
            verticalSpacing="default"
            horizontalSpacing="default"
            justifyContent="flex-end"
            marginTop="default"
          >
            <Button data-testid="remind-me-later-consent" variant="secondary" onClick={onRemindMeLater}>
              <Localized id="consent-update-remind-button" />
            </Button>
            <Button data-testid="confirm-consent" variant="primary" onClick={onConsentAcceptance}>
              <Localized id="consent-update-acknowledge-button" />
            </Button>
          </Flex>
        </Flex>
      </Processing>
    </motion.div>
  )
}

export default EversportsConsentUpdateForm
